'use client';

import NextLink from 'next/link';
import { ElementType } from 'react';
import Icon from '@/components/icon/Icon';
import { isExternal } from '@/utils/is-external';
import clsx from 'clsx';
import { getLinkHref, handleLinkClick } from '@/utils/link-helper-functions';
import {
  getRelAttributes,
  variantClasses,
  themeVariantClasses,
  sizeClasses,
  disabledClasses,
} from './const';
import type { CustomLinkProps } from './types';
export const Link = ({
  href,
  title,
  children,
  variant = 'tertiary',
  target,
  sizes = 'medium',
  leftIcon,
  rightIcon,
  nofollow = false,
  disabled = false,
  className,
  theme = 'light',
  ariaLabel,
  onClick,
}: CustomLinkProps) => {
  const rel = getRelAttributes(nofollow);

  // Determine if we should apply p-3 when only an icon is present
  const iconOnlyPaddingClass = !title && (leftIcon || rightIcon) ? 'p-3' : '';

  const classNames = clsx(
    'smzh-link rounded-lg flex flex-row items-center gap-2',
    rightIcon && !leftIcon ? 'text-left' : '',
    leftIcon && !rightIcon ? 'text-right' : '',
    theme === 'light' ? variantClasses[variant] : themeVariantClasses[variant],
    iconOnlyPaddingClass || sizeClasses[sizes], // Apply p-3 if only icon, otherwise size
    disabled ? disabledClasses : '',
    className,
  );

  const linkContent = (
    <>
      {leftIcon && <Icon {...leftIcon} />}
      {(children || title) && <span>{children || title}</span>}
      {rightIcon && <Icon {...rightIcon} />}
    </>
  );

  const LinkComponent: ElementType = isExternal(href) ? 'a' : NextLink;

  // Automatically set target based on link type if not explicitly provided
  const computedTarget = target ?? (isExternal(href) ? '_blank' : '_self');

  return (
    <LinkComponent
      href={getLinkHref(href, disabled)}
      target={computedTarget}
      rel={rel}
      aria-disabled={disabled ? 'true' : 'false'}
      tabIndex={disabled ? -1 : undefined}
      data-component-id="C011"
      className={classNames}
      aria-label={ariaLabel}
      onClick={(e) => {
        handleLinkClick(e, href, disabled);
        if (!e.defaultPrevented) {
          onClick?.(e);
        }
      }}
    >
      {linkContent}
    </LinkComponent>
  );
};
